import React, {MouseEventHandler, ReactElement, useEffect, useMemo, useState} from 'react';
import {
  AssetType,
  BoxLayer,
  BoxType,
  BoxTypeShape,
  CoreEvent,
  NumBool,
  Text,
  ViewMode,
} from '@taopix/taopix-core';
import {AssetMetaInterface, ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {
  AddPicturesIcon,
  BackgroundIcon,
  Button,
  EditBoxIcon,
  EmptyBoxIcon,
  TextBoxIcon,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import useViewMode from '../../../Hooks/useViewMode';
import {LayoutSelector} from '../../../Selectors';
import {useTaopix} from '../../Context/TaopixContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../Experience/ExperienceProvider';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import AlignToPage from './BoxSettingsOptions/AlignToPage';
import Arrange from './BoxSettingsOptions/Arrange';
import Background from './BoxSettingsOptions/Background';
import Border from './BoxSettingsOptions/Border';
import Enhance from './BoxSettingsOptions/Enhance';
import RotateAndFlip from './BoxSettingsOptions/RotateAndFlip';
import Shadow from './BoxSettingsOptions/Shadow';
import Shape from './BoxSettingsOptions/Shape';
import SizeAndPosition from './BoxSettingsOptions/SizeAndPosition';
import SwapPictures from './BoxSettingsOptions/SwapPictures';
import Transparency from './BoxSettingsOptions/Transparency';
import MaskPalette from '../MaskPalette';
import PaletteInformationMessage from './PaletteInformationMessage';

export type BoxSettingsPaletteProps = {
  sectionKey: string;
  item: ComputedPageItem;
  assets: AssetMetaInterface[];
};

type BoxSettingIntroButton = {
  visible?: boolean;
  label: string;
  startIcon: ReactElement;
  onClick: MouseEventHandler;
};

const BoxSettingsPalette = ({sectionKey, item, assets}: BoxSettingsPaletteProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const [viewMode] = useViewMode();
  const {t} = useTranslation(['boxpalettes']);
  const layout = useSelector(LayoutSelector);

  const {experience} = useExperience();

  const stateManager = window.Taopix.Core.get('stateManager');
  const display = stateManager.getPageDisplayProperties();

  const shape = useMemo(() => {
    return app?.core?.get('stateManager')?.getSelectedShape();
  }, [app?.core.get('stateManager')?.getSelectedShape()]);

  const [disabledBoxSettingIntroButton, setDisabledBoxSettingIntroButton] =
    useState<boolean>(false);

  const [boxSettingIntroButton, setBoxSettingIntroButton] = useState<BoxSettingIntroButton>({
    visible: false,
    label: '',
    startIcon: <></>,
    onClick: () => {},
  });
  const [showPalette, setShowPalette] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [clearPictureButton, setClearPictureButton] = useState<{
    visible: boolean;
    disabled: boolean;
  }>({
    visible: false,
    disabled: false,
  });

  useEffect(() => {
    const tokens: string[] = [];

    tokens.push(
      app.core.subscribe(CoreEvent.CONTEXT_OPTIONS_CLICK, (msg: string, data: any) => {
        ui.dispatch(UserInterfaceActions.setToolPaletteSection('box-settings-palette'));
      })
    );

    return () => {
      tokens.forEach((token, index) => {
        app.core.unsubscribe(token);
        delete tokens[index];
      });
    };
  }, [item?.ref]);

  // Get the type of selected item, show palette in toolbar, set relevent intro button
  useEffect(() => {
    if (item?.ref && item?.ref !== -1) {
      const boxType = item.type;
      if (boxType === BoxType.Image && item.layer === BoxLayer.Background) {
        setShowPalette(experience.EDITOR.backgroundOptions.backgroundOptionsPalette);
        setTitle(t('boxpalettes:str_TitleBackgroundBoxOptions'));
        setBoxSettingIntroButton({
          visible: experience.EDITOR.backgrounds.backgroundsPalette,
          label: t('boxpalettes:str_LabelSelectBackground'),
          startIcon: <BackgroundIcon />,
          onClick: () => {
            ui.dispatch(UserInterfaceActions.setToolPaletteSection('backgrounds'));
          },
        });
        setDisabledBoxSettingIntroButton(NumBool.True === item?.protectcontents);
        setClearPictureButton({
          visible: false,
          disabled: true,
        });
      } else if (
        boxType === BoxType.Image &&
        item?.asset?.assetType !== AssetType.Background &&
        item?.asset?.assetType !== AssetType.ScrapbookImage
      ) {
        setShowPalette(experience.EDITOR.pictureBoxOptions.pictureBoxOptionsPalette);
        setTitle(t('str_TitlePictureBoxOptions'));
        setBoxSettingIntroButton({
          visible: experience.EDITOR.pictures.picturePalette,
          label: t('str_LabelSelectPicture'),
          startIcon: <AddPicturesIcon />,
          onClick: () => {
            ui.dispatch(UserInterfaceActions.setToolPaletteSection('pictures'));
          },
        });
        setDisabledBoxSettingIntroButton(
          NumBool.True === item?.protectcontents || ui.state.swapActive
        );
        if (
          item.hirespath !== '' &&
          NumBool.False === item?.protectcontents &&
          !ui.state.swapActive
        ) {
          setClearPictureButton({
            visible: true,
            disabled: false,
          });
        } else {
          setClearPictureButton({
            visible: true,
            disabled: true,
          });
        }
      } else if (boxType === BoxType.Text) {
        setShowPalette(experience.EDITOR.textBoxOptions.textBoxOptionsPalette);
        setTitle(t('str_TitleTextBoxOptions'));
        setBoxSettingIntroButton({
          visible: experience.EDITOR.textBoxOptions.textBoxOptionsPalette,
          label: t('str_LabelEditText'),
          startIcon: <TextBoxIcon />,
          onClick: () => {
            Text.openTextEditor(shape, false, true, display);
            Text.deactivateClickType();
          },
        });
        setDisabledBoxSettingIntroButton(NumBool.True === item?.protectcontents);
        setClearPictureButton({
          visible: false,
          disabled: true,
        });
      } else if (
        boxType === BoxType.Shape &&
        [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Polygon, BoxTypeShape.Rect].includes(
          item?.shape
        )
      ) {
        setShowPalette(experience.EDITOR.shapeBoxOptions.shapeBoxOptionsPalette);
        setTitle(t('str_TitleShapeBoxOptions'));
        setBoxSettingIntroButton({
          ...boxSettingIntroButton,
          visible: false,
        });
        setDisabledBoxSettingIntroButton(false);
        setClearPictureButton({
          visible: false,
          disabled: true,
        });
      } else if (AssetType.ScrapbookImage === item.hiresfiletype) {
        setShowPalette(experience.EDITOR.stickerOptions.stickerOptionsPalette);
        setTitle(t('str_TitleStickerBoxOptions'));
        setBoxSettingIntroButton({
          ...boxSettingIntroButton,
          visible: false,
        });
        setDisabledBoxSettingIntroButton(false);
        setClearPictureButton({
          visible: false,
          disabled: true,
        });
      }
    }
  }, [item?.asset, item?.asset?.assetType, item?.image, item?.shape, item?.type]);

  const primaryActionButtonWrapper = classNames('flex', 'flex-col', 'mb-sm', 'space-y-sm');

  const buttonSizingClasses = classNames('px-xs', 'shrink-0', 'w-full');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'overflow-y-auto',
    'pb-lg',
    'px-md',
    'space-y-xs',
    'sm:h-mobilePaletteContentHeight',
    'lg:flex-1'
  );

  useEffect(() => {
    if (item?.ref === -1) {
      if (ui.state.palette.section === sectionKey) {
        // If no item is selected and we are currently at the box palette root level
        // Show the 'select an item' message (closing the whole palette is too abrupt)
        setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
      } else if (!ui.state.palette.section?.includes(sectionKey)) {
        // If no item is selected and we aren't in this section
        // hide the Box Settings tool palette button
        setShowPalette(false);
      }
    } else {
      // When an item is selected and we want to show the palette, clear the message. If the section is not available close the palette all together.
      if (!showPalette && ui.state.palette.section?.includes(sectionKey)) {
        ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
      } else {
        setNotAvailableMessage('');
      }
    }
    if (ViewMode.Grid === viewMode && !layout.retroprints) {
      setNotAvailableMessage(t('*:str_MessagePaletteUnavailable'));
    }
  }, [item.ref, ui.state.palette.section, viewMode, showPalette]);

  useEffect(() => {
    setDisabledBoxSettingIntroButton(ui.state.swapActive);

    const visible =
      item.type === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage;

    setClearPictureButton({
      visible: visible,
      disabled: ui.state.swapActive,
    });
  }, [ui.state.swapActive]);

  return (
    <ToolPaletteSection
      sectionKey={sectionKey}
      title={title}
      buttonIcon={<EditBoxIcon />}
      showButton={!ui.state.isSmallScreen && showPalette}
      id={'boxSettingsPalette'}
      data-testid={'box-settings-palette-section'}
      useFocusMode
    >
      <div className={wrapperClasses}>
        {notAvailableMessage !== '' ? (
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          <>
            {(boxSettingIntroButton.visible || clearPictureButton.visible) && (
              <div className={primaryActionButtonWrapper}>
                {boxSettingIntroButton.visible && (
                  <Button
                    label={boxSettingIntroButton.label}
                    startIcon={boxSettingIntroButton.startIcon}
                    buttonStyle={'secondary'}
                    labelLines={3}
                    className={buttonSizingClasses}
                    onClick={boxSettingIntroButton.onClick}
                    disabled={disabledBoxSettingIntroButton}
                    data-testid={'box-settings-palette-intro-button'}
                  />
                )}
                {experience.EDITOR.pictureBoxOptions.clearPictureButton &&
                  clearPictureButton.visible && (
                    <Button
                      label={t('*:str_LabelClearPictureBox')}
                      startIcon={<EmptyBoxIcon />}
                      buttonStyle={'tertiary'}
                      labelLines={3}
                      className={buttonSizingClasses}
                      onClick={() => {
                        app.core.dispatch('SHAPE.CLEAR_PICTURE_BOX', null).catch(error => {
                          console.error(error);
                        });
                      }}
                      disabled={clearPictureButton.disabled}
                      data-testid={'box-settings-palette-delete-image-button'}
                    />
                  )}
                <SwapPictures sectionKey={'swap-pictures'} item={item} />
              </div>
            )}
            <RotateAndFlip sectionKey={'rotate-and-flip'} item={item} />
            <Background sectionKey={'background'} item={item} />
            <Enhance sectionKey={'enhance'} item={item} assets={assets} />
            <Border sectionKey={'border'} item={item} />
            <Shadow sectionKey={'shadow'} item={item} />
            <MaskPalette sectionKey={'mask'} item={item} />
            <Transparency sectionKey={'transparency'} item={item} />
            {ui.state.canEditPageLayout && <AlignToPage sectionKey={'align-to-page'} item={item} />}
            {ui.state.canEditPageLayout && <Arrange sectionKey={'arrange'} item={item} />}
            {ui.state.canEditPageLayout && (
              <SizeAndPosition sectionKey={'size-and-position'} item={item} />
            )}
            {ui.state.canEditPageLayout && <Shape sectionKey={'shape'} item={item} />}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default BoxSettingsPalette;
